import { Fragment, useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

import MoreButton from './display/MoreButton'

const Wrapper = styled.div<{ inline?: boolean }>`
  letter-spacing: normal;

  ${({ inline }) =>
    inline &&
    `
    ${MoreButton} {
      display: inline-block;
      margin: 0;
      padding: 0;
      line-height: 1;
    }
  `}
`

const ClampedContent = styled.div<{ lines: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines }) => lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const formatLineBreaks = (text: string) =>
  text &&
  text
    .trim()
    .split('\n')
    .map((line, i, arr) => (
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </Fragment>
    ))

const APPROX_CHARS_PER_LINE = 55

// Seeds the SSR/first-render toggle state without a DOM measurement; the effect
// in the component corrects it on mount.
export const estimateTruncated = (text: string, lines: number) => {
  // Trim to match formatLineBreaks' trimmed render.
  const trimmed = text?.trim()
  return (
    !!trimmed &&
    (trimmed.split('\n').length > lines ||
      trimmed.length > lines * APPROX_CHARS_PER_LINE)
  )
}

type ShowMoreTextProps = {
  lines?: number
  inline?: boolean
  children: string
}

const ShowMoreText = ({ lines = 3, inline, children }: ShowMoreTextProps) => {
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(() =>
    estimateTruncated(children, lines),
  )
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = contentRef.current
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  }, [children, lines])

  return (
    <Wrapper inline={inline}>
      {expanded ? (
        <div>{formatLineBreaks(children)}</div>
      ) : (
        <ClampedContent ref={contentRef} lines={lines}>
          {formatLineBreaks(children)}
        </ClampedContent>
      )}
      {(isTruncated || expanded) && (
        <MoreButton type="button" onClick={() => setExpanded(!expanded)}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {expanded
            ? inline
              ? 'Less'
              : '- Show Less'
            : inline
              ? 'More'
              : '+ Show More'}
        </MoreButton>
      )}
    </Wrapper>
  )
}

export default ShowMoreText
